import { useQuery, useMutation, useQueries } from "react-query"
import qs from "query-string"
import toast from "react-hot-toast"

import { apiRequest } from "util/request"

/** ------------------- **/
/*  Products
/** ------------------- **/

export function useProduct({ companyId, productId }) {
  const cacheKey = ["product", companyId, productId]
  const query = () => apiRequest(`products/${companyId}/${productId}`)
  return useQuery(cacheKey, query, { enabled: !!companyId && !!productId })
}

export function useMultipleProductDetails({ competitorProductAndCompanyIds }) {
  const useQueriesArray =
    competitorProductAndCompanyIds?.map(productAndCompanyIds => ({
      queryKey: ["product", productAndCompanyIds[0], productAndCompanyIds[1]],
      queryFn: () =>
        apiRequest(
          `products/${productAndCompanyIds[0]}/${productAndCompanyIds[1]}`
        ),
    })) ?? []
  return useQueries({ queries: useQueriesArray })
}

export function useProductSales({ companyId, productId, startDate, endDate }) {
  const paramsString = qs.stringify({
    start_date: startDate,
    end_date: endDate,
  })
  const cacheKey = ["productSales", companyId, productId, startDate, endDate]
  const query = () =>
    apiRequest(`products/${companyId}/${productId}/sales?${paramsString}`)
  return useQuery(cacheKey, query, { enabled: !!companyId && !!productId })
}

export function useMultipleProductSales({
  competitorProductAndCompanyIds,
  startDate,
  endDate,
}) {
  const paramsString = qs.stringify({
    start_date: startDate,
    end_date: endDate,
  })
  const useQueriesArray =
    competitorProductAndCompanyIds?.map(productAndCompanyIds => ({
      queryKey: [
        "productSales",
        productAndCompanyIds[0],
        productAndCompanyIds[1],
        startDate,
        endDate,
      ],
      queryFn: () =>
        apiRequest(
          `products/${productAndCompanyIds[0]}/${productAndCompanyIds[1]}/sales?${paramsString}`
        ),
    })) ?? []
  return useQueries({ queries: useQueriesArray })
}

export function useProductVerticals({ debouncedSearchTerm }) {
  const paramString = qs.stringify({
    search_term: debouncedSearchTerm,
  })

  const cacheKey = ["verticals", debouncedSearchTerm]
  const query = () => apiRequest(`products/verticals?${paramString}`)
  return useQuery(cacheKey, query, {
    enabled: debouncedSearchTerm?.length > 2,
  })
}

export function useProductSearch({
  companyId,
  debouncedSearchTerm,
  pageSize,
  pageOffset,
}) {
  const paramString = qs.stringify({
    title: debouncedSearchTerm,
    company_id: companyId,
    page_size: pageSize,
    page_number: pageOffset,
  })
  const cacheKey = [
    "searchProducts",
    companyId,
    debouncedSearchTerm,
    pageSize,
    pageOffset,
  ]
  const query = () => apiRequest(`products/search?${paramString}`)
  return useQuery(cacheKey, query, {
    enabled: !!companyId && debouncedSearchTerm?.length > 2,
  })
}

export function useRecentProductEvents({ companyId }) {
  const cacheKey = ["recentProductEvents", companyId]
  const query = () => apiRequest(`companies/${companyId}/recent_events`)
  return useQuery(cacheKey, query, {
    enabled: !!companyId,
  })
}

export function useSuggestedProducts({ productId }) {
  const paramString = qs.stringify({
    product_id: productId,
  })
  const cacheKey = ["suggestedProducts", productId]
  const query = () => apiRequest(`products/competitors?${paramString}`)
  return useQuery(cacheKey, query, {
    enabled: !!productId,
  })
}
